import React, { useEffect, useState } from 'react';
import wingitDesign from './designSystem';
import { listSelectableOrganisations } from './organisationMembershipService';
import {
  clearActiveOrganisationId,
  getOrganisationSelectionStorageKey,
  setActiveOrganisationSelection,
} from './organisationContext';

export default function OrganisationContextSelector({ userId, isMobile = false }) {
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const storageKey = userId ? getOrganisationSelectionStorageKey(userId) : '';

  useEffect(() => {
    let cancelled = false;

    async function loadOrganisations() {
      if (!userId) {
        clearActiveOrganisationId();
        setOrganisations([]);
        setSelectedOrganisationId('');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setOrganisations([]);
      setSelectedOrganisationId('');

      try {
        const options = await listSelectableOrganisations();
        const storedOrganisationId = window.sessionStorage.getItem(storageKey);
        const storedSelection = options.find(
          (organisation) => String(organisation.organisation_id) === storedOrganisationId
        );
        const defaultSelection = options.find(
          (organisation) => organisation.source === 'legacy'
        ) || options[0];
        const selectedOrganisation = storedSelection || defaultSelection;
        const selectedId = selectedOrganisation
          ? String(selectedOrganisation.organisation_id)
          : '';

        if (storedOrganisationId && !storedSelection) {
          window.sessionStorage.removeItem(storageKey);
        }
        if (selectedId) {
          setActiveOrganisationSelection(userId, selectedId);
        } else {
          clearActiveOrganisationId();
        }

        if (!cancelled) {
          setOrganisations(options);
          setSelectedOrganisationId(selectedId);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Could not load your organisations.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOrganisations();
    return () => {
      cancelled = true;
    };
  }, [storageKey, userId]);

  function handleSelectionChange(event) {
    const nextOrganisation = organisations.find(
      (organisation) => String(organisation.organisation_id) === event.target.value
    );

    if (!nextOrganisation) {
      return;
    }

    try {
      const nextId = String(nextOrganisation.organisation_id);
      setActiveOrganisationSelection(userId, nextId);
      setSelectedOrganisationId(nextId);
      setError('');
    } catch (storageError) {
      setError(storageError.message || 'Could not save this browser-session selection.');
    }
  }

  const selectedOrganisation = organisations.find(
    (organisation) => String(organisation.organisation_id) === selectedOrganisationId
  );

  return (
    <section
      aria-labelledby="dashboard-organisation-selector-heading"
      style={{
        ...wingitDesign.cards.section,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: wingitDesign.spacing.md,
        textAlign: 'left',
      }}
    >
      <div style={{ flex: '1 1 260px' }}>
        <p
          style={{
            ...wingitDesign.typography.caption,
            color: wingitDesign.colors.primary,
            margin: '0 0 0.35rem',
          }}
        >
          Organisation selection
        </p>
        <h2
          id="dashboard-organisation-selector-heading"
          style={{
            ...wingitDesign.typography.sectionTitle,
            color: wingitDesign.colors.text,
            margin: 0,
          }}
        >
          {selectedOrganisation
            ? `Selected: ${selectedOrganisation.organisation_name}`
            : 'Choose an organisation'}
        </h2>
        <p
          style={{
            color: wingitDesign.colors.textMuted,
            fontSize: '0.82rem',
            lineHeight: 1.5,
            margin: '0.35rem 0 0',
          }}
        >
          Aircraft and booking reads follow this browser-session selection. Booking
          changes, training, roles, and permissions remain on your existing organisation.
        </p>
        {error && (
          <p role="alert" style={{ color: wingitDesign.colors.danger, margin: '0.4rem 0 0' }}>
            {error}
          </p>
        )}
      </div>

      <label
        htmlFor="dashboard-organisation-selector"
        style={{
          display: 'grid',
          flex: '0 1 320px',
          gap: '0.35rem',
          color: wingitDesign.colors.textSoft,
          fontSize: '0.82rem',
          fontWeight: 700,
        }}
      >
        Selected organisation for this session
        <select
          id="dashboard-organisation-selector"
          aria-label="Selected organisation for this session"
          value={selectedOrganisationId}
          onChange={handleSelectionChange}
          disabled={loading || organisations.length === 0}
          style={{
            width: '100%',
            minHeight: 42,
            padding: '0.6rem 0.75rem',
            border: `1px solid ${wingitDesign.colors.borderStrong}`,
            borderRadius: wingitDesign.radius.small,
            background: wingitDesign.colors.surfaceElevated,
            color: wingitDesign.colors.text,
            font: 'inherit',
          }}
        >
          {loading && <option value="">Loading organisations...</option>}
          {!loading && organisations.length === 0 && (
            <option value="">No current or approved organisations</option>
          )}
          {organisations.map((organisation) => (
            <option
              key={organisation.organisation_id}
              value={organisation.organisation_id}
            >
              {organisation.organisation_name}
              {organisation.source === 'legacy'
                ? ' — Current organisation'
                : ' — Approved membership'}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}
